import { useNiceStore as useStore, PollingStatus } from '@/stores/NiceStore'
import { storeToRefs } from 'pinia'
import { ApiError, ApiErrorType } from '@/model/Storage'
import type {
    DeduplicationOptions,
    SimilarityDetails,
    ProcessingStats,
    ActivityLogEntry,
    DeduplicationStage,
    DeduplicationSubstage,
    DeduplicationPriority,
    DeduplicationMethod,
    DeduplicationJobStatus,
    RequestStatus,
} from '@/stores/NiceStore'

/**
 * Composable bridge between Pinia store and frontend
 * Provides reactive access to the NiceStore state and methods
 * with enhanced TypeScript typing and error handling
 */
export default function useNiceStore() {
    const niceStore = useStore()
    const {
        // Discriminated union states
        requestStatus,
        jobStatus,

        // Report data
        deduplicationReport,
        similarityDetails,

        // Progress tracking
        loading,
        progress,
        statusMessage,
        errorMessage,
        currentStage,
        currentSubstage,
        elapsedSeconds,
        etaSeconds,
        totalVideos,
        processedVideos,
        duplicateCount,

        // Computed values
        detailedStatusMessage,
        stageName,
        substageName,
        formattedElapsedTime,
        formattedEtaTime,

        // Additional data
        processingStats,
        activityLog,
        latestActivities,
        stageTimes,

        // Status descriptions
        stageDescription,
        substageDescription,
        cacheStatus,

        // Metadata
        jobId,
        pollCount,
        pollingStatus,
    } = storeToRefs(niceStore)

    /**
     * Run the deduplication process
     * @param userId User ID to associate with the deduplication job
     * @param jobId Job ID to track progress
     * @param options Configuration options for the deduplication process
     * @returns Promise that resolves when the job is started
     * @throws ApiError when the job cannot be started
     */
    const runDeduplication = async (
        userId: string,
        jobId: string,
        options?: DeduplicationOptions
    ): Promise<void> => {
        await niceStore.runDeduplication(userId, jobId, options)
    }

    /**
     * Reset the deduplication state to its initial values
     */
    const resetState = (): void => {
        niceStore.resetState()
    }

    /**
     * Get similarity details for a specific pair of videos
     * @param mediaId1 First media ID
     * @param mediaId2 Second media ID
     * @returns Promise that resolves to the similarity details or null if not found
     */
    const getSimilarityDetails = async (
        mediaId1: string,
        mediaId2: string
    ): Promise<SimilarityDetails | null> => {
        return niceStore.getSimilarityDetails(mediaId1, mediaId2)
    }

    /**
     * Explicitly fetch the deduplication report for a job
     * @param jobId The job ID to fetch the report for
     * @returns Promise that resolves to a boolean indicating if the report was successfully fetched
     * @throws ApiError when the fetch fails after configured retries
     */
    const fetchDeduplicationReport = async (jobId: string): Promise<boolean> => {
        return niceStore.fetchDeduplicationReport(jobId)
    }

    /**
     * Start polling for deduplication status updates
     * @param jobId The job ID to poll status for
     * @returns Promise that resolves when polling is complete
     */
    const pollDeduplicationStatus = async (jobId: string): Promise<void> => {
        return niceStore.pollDeduplicationStatus(jobId)
    }

    /**
     * Get the description for a specific stage
     * @param stage The stage to get the description for
     * @returns The stage description
     */
    const getStageDescription = (stage: DeduplicationStage | string): string => {
        return niceStore.getStageDescription(stage)
    }

    /**
     * Get the description for a specific substage
     * @param substage The substage to get the description for
     * @returns The substage description
     */
    const getSubstageDescription = (substage: DeduplicationSubstage | string): string => {
        return niceStore.getSubstageDescription(substage)
    }

    /**
     * Format a media ID for display
     * @param mediaId The media ID to format
     * @returns The formatted media ID
     */
    const formatMediaId = (mediaId: string): string => {
        if (!mediaId) return ''

        // If it's a short ID, return as is
        if (mediaId.length <= 10) return mediaId

        // If it's a long ID, show first 6 and last 4 characters
        return `${mediaId.substring(0, 6)}...${mediaId.substring(mediaId.length - 4)}`
    }

    /**
     * Check if the job is currently loading
     * @returns True if the job is loading, false otherwise
     */
    const isLoading = (): boolean => {
        return requestStatus.value.state === 'loading' || loading.value === true
    }

    /**
     * Check if the job is in an error state
     * @returns True if the job is in an error state, false otherwise
     */
    const hasError = (): boolean => {
        return (
            requestStatus.value.state === 'error' ||
            jobStatus.value.status === 'error' ||
            !!errorMessage.value
        )
    }

    /**
     * Get the current error message if any
     * @returns The current error message or empty string
     */
    const getErrorMessage = (): string => {
        if (requestStatus.value.state === 'error') {
            return requestStatus.value.error
        }
        if (jobStatus.value.status === 'error') {
            return jobStatus.value.message
        }
        return errorMessage.value || ''
    }

    /**
     * Check if the job is completed
     * @returns True if the job is completed, false otherwise
     */
    const isCompleted = (): boolean => {
        return (
            jobStatus.value.status === 'completed' ||
            pollingStatus.value === PollingStatus.COMPLETED
        )
    }

    return {
        // Discriminated union states
        requestStatus,
        jobStatus,

        // Report data
        deduplicationReport,
        similarityDetails,

        // Progress tracking
        loading,
        progress,
        statusMessage,
        errorMessage,
        currentStage,
        currentSubstage,
        elapsedSeconds,
        etaSeconds,
        totalVideos,
        processedVideos,
        duplicateCount,
        jobId,
        pollCount,
        pollingStatus,

        // Additional data
        processingStats,
        activityLog,
        stageTimes,

        // Getters
        detailedStatusMessage,
        stageName,
        substageName,
        formattedElapsedTime,
        formattedEtaTime,
        latestActivities,
        stageDescription,
        substageDescription,
        cacheStatus,

        // Actions
        runDeduplication,
        resetState,
        getSimilarityDetails,
        fetchDeduplicationReport,
        pollDeduplicationStatus,

        // Helpers
        getStageDescription,
        getSubstageDescription,
        formatMediaId,

        // New helper methods
        isLoading,
        hasError,
        getErrorMessage,
        isCompleted,

        // Error handling types (for use in consuming components)
        ApiError,
        ApiErrorType,
    }
}
